import {ViewManagerModel} from '@xh/hoist/cmp/viewmanager';
import {HoistModel, managed, TaskObserver, XH} from '@xh/hoist/core';
import {DashCanvasModel} from '@xh/hoist/desktop/cmp/dash';
import {Icon} from '@xh/hoist/icon';
import {action, observable, runInAction, when} from '@xh/hoist/mobx';
import {SECONDS} from '@xh/hoist/utils/datetime';
import {State} from 'router5';
import {SpecAssistantModel} from '../../cmp/spec/assistant/SpecAssistantModel';
import {ResearchWidgetKind} from '../../data/ResearchTypes';
import type {RzxAppModel} from '../../RzxAppModel';
import {DashboardSpecModel} from './dashboardSpec/DashboardSpecModel';
import dashboardSpecOpsSchema from './dashboardSpec/dashboardSpecOps.schema.json';
import {DASHBOARD_SPEC_VERSION} from './dashboardSpec/DashboardSpecTypes';
import {DEFAULT_WIDGET_SIZE, defaultDashboardState, RESEARCH_PRESETS, ResearchPreset} from './ResearchPresets';
import {researchWidget} from './widgets/ResearchWidget';
import type {ResearchWidgetModel} from './widgets/ResearchWidgetModel';

export type ResearchTickerStatus = 'none' | 'loading' | 'ready' | 'unsupported' | 'error';

/**
 * The Research tab: a canvas of grid and chart widgets over the research datasets, all showing the
 * selected ticker. The canvas - which widgets, where, and each one's data and spec - is a dashboard
 * saved through the app's research ViewManager, so a user can keep several and share them. The AI
 * dashboard assistant builds and changes it through a DashboardSpec.
 */
export class ResearchTabModel extends HoistModel {
    readonly viewManagerModel: ViewManagerModel = (XH.appModel as RzxAppModel).researchViewManagerModel;

    // Declared ahead of the models below, whose initializers read them - an accessor read before its own
    // initializer has run throws.
    @observable accessor isEditable = false;
    @observable accessor showAssistant = false;
    @observable accessor ticker: string = null;
    @observable accessor tickerStatus: ResearchTickerStatus = 'none';
    /** Incremented when the widgets should load their rows again. */
    @observable accessor dataRevision = 0;

    @managed dashCanvasModel = this.createDashCanvasModel();
    @managed ensureTask = TaskObserver.trackLast({message: 'Loading research data...'});
    @managed dashboardSpecModel = this.createDashboardSpecModel();
    @managed assistantModel = this.createAssistantModel();

    /** The widgets on the canvas, by view id - observable, so code can wait for a widget the canvas has just added. */
    private readonly widgetModels = observable.map<string, ResearchWidgetModel>({}, {deep: false});

    constructor() {
        super();

        this.addReaction({
            track: () => XH.routerState,
            run: state => {
                if (state.name === 'default.research') this.handleRouterAction(state);
            },
            fireImmediately: true
        });

        // Another dashboard - what the assistant did to the previous one can't be undone on this one
        this.addReaction({
            track: () => this.viewManagerModel.view?.token,
            run: () => {
                this.dashboardSpecModel.clearHistory();
                this.assistantModel.clearHistory();
            }
        });
    }

    /** The ticker, once the backend holds its research data - what the widgets load. */
    get readyTicker(): string {
        return this.tickerStatus === 'ready' ? this.ticker : null;
    }

    /** Why the widgets have nothing to show for the ticker, if so. */
    get tickerProblem(): string {
        switch (this.tickerStatus) {
            case 'unsupported':
                return `No research data for ${this.ticker} - it is not a company or a fund.`;
            case 'error':
                return `Research data for ${this.ticker} could not be loaded.`;
            default:
                return null;
        }
    }

    @action
    toggleEditable() {
        this.isEditable = !this.isEditable;
        this.dashCanvasModel.layoutLocked = !this.isEditable;
        this.dashCanvasModel.renameLocked = !this.isEditable;
        this.dashCanvasModel.contentLocked = !this.isEditable;
    }

    @action
    toggleAssistant() {
        this.showAssistant = !this.showAssistant;
    }

    @action
    loadResearch(ticker: string) {
        if (!ticker) return;
        this.ticker = ticker.toUpperCase();
        this.tickerStatus = 'loading';
        this.ensureTickerAsync(this.ticker).linkTo(this.ensureTask);
    }

    /** Load the widgets' rows again from the backend. */
    @action
    refresh() {
        XH.researchService.clearCache();
        this.dataRevision++;
    }

    registerWidget(id: string, widget: ResearchWidgetModel) {
        runInAction(() => this.widgetModels.set(id, widget));
    }

    unregisterWidget(id: string, widget: ResearchWidgetModel) {
        if (this.widgetModels.get(id) === widget) runInAction(() => this.widgetModels.delete(id));
    }

    addWidget(kind: ResearchWidgetKind) {
        this.dashCanvasModel.addView(kind);
    }

    addPreset({kind, title, state, width, height}: ResearchPreset) {
        this.dashCanvasModel.addView(kind, {title, state, width, height});
    }

    /** Create and switch to a new, empty dashboard - opened for editing so widgets can be added straight away. */
    async newDashboardAsync() {
        const vm = this.viewManagerModel;

        if (vm.isValueDirty && vm.view.isOwned) {
            const discard = await XH.confirm({
                message: 'You have unsaved changes to this dashboard. Discard them and create a new one?',
                confirmProps: {text: 'Discard changes', intent: 'danger'},
                cancelProps: {text: 'Cancel', autoFocus: true}
            });
            if (!discard) return;
        }

        const name = await XH.prompt<string>({
            title: 'New dashboard',
            icon: Icon.add(),
            message: 'Name for the new dashboard:',
            input: {rules: [({value}) => vm.validateViewNameAsync(value, null, false)]},
            confirmProps: {text: 'Create', intent: 'primary'}
        });
        if (!name) return;

        try {
            await vm
                .saveAsAsync({
                    name: name.trim(),
                    group: null,
                    description: null,
                    isShared: false,
                    isGlobal: false,
                    value: {dashCanvas: {state: []}}
                })
                .linkTo(vm.saveTask);
            // saveAsAsync only switches the view (it's built for Save As, keeping what's on screen) - load it to clear the canvas
            await vm.resetAsync();
            if (!this.isEditable) this.toggleEditable();
        } catch (e) {
            XH.handleException(e, {title: 'Error creating dashboard'});
        }
    }

    //------------------------
    // Implementation
    //------------------------
    /**
     * A ticker the backend holds no research data for yet has every dataset fetched first, and a stale one
     * its stale datasets, so the widgets only load once the data is there.
     */
    private async ensureTickerAsync(ticker: string) {
        try {
            const {status, succeeded} = await XH.researchService.ensureTickerAsync(ticker);
            if (ticker !== this.ticker) return;

            if (status === 'Loaded') XH.successToast(`Fetched research data for ${ticker} (${succeeded} datasets).`);
            if (status === 'Failed') XH.dangerToast(`Research data for ${ticker} could not be fetched. Try again later.`);

            runInAction(() => {
                this.tickerStatus = status === 'Unsupported' ? 'unsupported' : 'ready';
                this.dataRevision++;
            });
        } catch (e) {
            if (ticker !== this.ticker) return;
            runInAction(() => (this.tickerStatus = 'error'));
            XH.handleException(e, {title: 'Error loading research', showAlert: false});
        }
    }

    private createDashCanvasModel() {
        return new DashCanvasModel({
            persistWith: {viewManagerModel: this.viewManagerModel},
            columns: 40,
            rowHeight: 20,
            layoutLocked: !this.isEditable,
            renameLocked: !this.isEditable,
            contentLocked: !this.isEditable,
            emptyText: 'Add widgets to the dashboard',
            initialState: defaultDashboardState(),
            viewSpecDefaults: DEFAULT_WIDGET_SIZE,
            viewSpecs: [
                {id: 'grid', title: 'Grid', icon: Icon.grid(), content: () => researchWidget({kind: 'grid'})},
                {id: 'chart', title: 'Chart', icon: Icon.chartLine(), content: () => researchWidget({kind: 'chart'})}
            ]
        });
    }

    private createDashboardSpecModel() {
        const {columns, rowHeight} = this.dashCanvasModel;
        return new DashboardSpecModel(this.dashCanvasModel, {columns, rowHeight, datasets: XH.researchService.datasets, presets: RESEARCH_PRESETS});
    }

    private createAssistantModel() {
        const specModel = this.dashboardSpecModel;
        return new SpecAssistantModel({
            kind: 'dashboard',
            specModel,
            schemaVersion: DASHBOARD_SPEC_VERSION,
            outputSchema: dashboardSpecOpsSchema,
            target: () => specModel.buildTarget(),
            title: () => this.viewManagerModel.view?.name,
            // The second pass: each widget the dashboard assistant planned is finished by its own assistant (0038)
            followUp: () => this.refineWidgetsAsync(),
            followUpMessage: 'Refining the new widgets...',
            hint: 'Describe the dashboard you want, or a change to this one - e.g. "build a valuation dashboard", or "add a chart of annual revenue and net income". Each widget has its own assistant for its look: its menu, Configure.'
        });
    }

    /**
     * Have each widget the last change gave a purpose finish itself for it, all at once - a widget
     * the canvas has only just added is waited for. Returns a line per widget.
     */
    private async refineWidgetsAsync(): Promise<string[]> {
        const lines = await Promise.all(
            this.dashboardSpecModel.widgetsToRefine.map(async ({id, purpose}) => {
                try {
                    await when(() => this.widgetModels.has(id), {timeout: 10 * SECONDS});
                } catch {
                    return null;
                }
                return this.widgetModels.get(id).refineAsync(purpose);
            })
        );
        return lines.filter(Boolean);
    }

    private handleRouterAction(state: State) {
        const {params} = state,
            {ticker} = params;

        if (ticker) this.loadResearch(ticker);
    }
}
